import { useRemoteData, useStrictParams } from '@binhatch/hooks';
import { translations } from '@binhatch/locale';
import { DynamicBackButton, OrderDetail, PageLoaderHeading } from '@binhatch/ui';
import { OrderStatus } from '@flexinet/flexinet-api';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { clientApi, orderApi, userApi } from '@/integrations/api';
import { urls } from '@/utils/url';

interface CancellationErrorDetail {
  code?: string;
  message?: string;
}

interface CancellationLineError {
  errorCode?: string;
  errorMessage?: string;
  productCode?: string;
}

interface ParsedCancellationReason {
  errorDetail?: CancellationErrorDetail;
  lineErrors?: CancellationLineError[];
}

type CancellationKind = 'webshop_promotion' | 'sales_budget';

const WEBSHOP_PROMOTION_KINDS = new Set(['webshop', 'promotion']);

const getCancellationKind = (kind?: string): CancellationKind => {
  const normalized = kind?.toLowerCase();
  return normalized === 'sales_budget' ? 'sales_budget' : WEBSHOP_PROMOTION_KINDS.has(normalized ?? '') ? 'webshop_promotion' : 'webshop_promotion';
};

const getErrorCodeTranslationId = (kind: CancellationKind, errorCode?: string) => {
  if (!errorCode) return undefined;

  const salesBudgetErrorCodes = translations.components.orderDetail.cancellation.errorCodes.salesBudget as Record<string, string>;
  const webshopPromotionErrorCodes =
    translations.components.orderDetail.cancellation.errorCodes.webshopPromotion as Record<string, string>;

  return kind === 'sales_budget' ? salesBudgetErrorCodes[errorCode] : webshopPromotionErrorCodes[errorCode];
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === 'object' && !Array.isArray(value);
};

const getStringField = (record: Record<string, unknown>, field: string): string | undefined => {
  const value = record[field];
  return typeof value === 'string' ? value : undefined;
};

const parseCancellationErrorDetail = (value: unknown): CancellationErrorDetail | undefined => {
  if (!isRecord(value)) return undefined;

  const code = getStringField(value, 'code');
  const message = getStringField(value, 'message');

  if (!code && !message) return undefined;

  return { code, message };
};

const parseCancellationLineError = (value: unknown): CancellationLineError | undefined => {
  if (!isRecord(value)) return undefined;

  const errorCode = getStringField(value, 'errorCode');
  const errorMessage = getStringField(value, 'errorMessage');
  const productCode = getStringField(value, 'productCode');

  if (!errorCode && !errorMessage && !productCode) return undefined;

  return { errorCode, errorMessage, productCode };
};

const parseCancellationReason = (cancellingReason?: unknown): ParsedCancellationReason | null => {
  if (!isRecord(cancellingReason)) return null;

  const message = getStringField(cancellingReason, 'message');
  if (!message || typeof message !== 'string') return null;

  try {
    const parsed = JSON.parse(message) as unknown;
    if (!isRecord(parsed)) return null;

    const errorDetail = parseCancellationErrorDetail(parsed.errorDetail);
    const lineErrors = Array.isArray(parsed.lineErrors)
      ? parsed.lineErrors
          .map((lineError) => parseCancellationLineError(lineError))
          .filter((lineError): lineError is CancellationLineError => !!lineError)
      : [];

    if (!errorDetail && !lineErrors.length) return null;

    return {
      errorDetail,
      lineErrors
    };
  } catch {
    return null;
  }
};

export const OrderDetailPage: React.FC = () => {
  const intl = useIntl();
  const { orderId } = useStrictParams<{ orderId: string }>();

  const order = useRemoteData({ key: `useOrder`, orderId }, async ({ orderId }) => {
    return orderApi.getOrder(orderId).then((r) => r.data);
  });

  const userId = order.data?.userID;

  const user = useRemoteData({ key: `useUser`, userId, skip: !userId }, async ({ userId }) => {
    if (!userId) return;
    return userApi.getUserByID(userId).then((r) => ({ user: r.data, url: urls.sales.getOne({ userId }) }));
  });

  const clientId = user.data?.user.clientId;

  const client = useRemoteData({ key: `useClient`, clientId, skip: !clientId }, async ({ clientId }) => {
    if (!clientId) return;
    return clientApi.getClient(clientId).then((r) => ({ client: r.data, url: urls.clients.getOne({ clientId }) }));
  });

  const cancellationReason = React.useMemo(
    () => parseCancellationReason((order.data as { cancellingReason?: unknown } | undefined)?.cancellingReason),
    [order.data]
  );

  const isCancelledOrder = order.data?.status === OrderStatus.Cancelled;
  const cancellationKind = getCancellationKind((order.data as { kind?: string } | undefined)?.kind);

  const getTranslatedErrorCode = (errorCode?: string) => {
    const id = getErrorCodeTranslationId(cancellationKind, errorCode);
    if (!id) return '-';

    return intl.formatMessage({ id });
  };

  return (
    <main className="space-y-6">
      <div>
        <DynamicBackButton />

        <PageLoaderHeading className="flex-1" loading={order.isLoading || order.isValidating}>
          <FormattedMessage id={translations.components.orderDetail.title} />
          {order.data?.id ? ` #${order.data?.id}` : ''}
        </PageLoaderHeading>
      </div>

      <OrderDetail
        client={client.data}
        getProductUrl={(productId) => urls.shop.products.getOne({ productId })}
        loading={order.isLoading || order.isValidating}
        order={order.data}
        user={user.data}
      />

      {isCancelledOrder && cancellationReason && (
        <section className="rounded-lg border border-error/30 bg-error/5 p-4">
          <h2 className="text-error-light font-semibold">
            <FormattedMessage id={translations.components.orderDetail.cancellation.title} />
          </h2>

          <div className="mt-3 space-y-1 text-sm">
            <div>
              <span className="font-medium">
                <FormattedMessage id={translations.components.orderDetail.cancellation.code} />
              </span>{' '}
              {cancellationReason.errorDetail?.code ?? '-'}
              {cancellationReason.errorDetail?.code ? ` (${getTranslatedErrorCode(cancellationReason.errorDetail.code)})` : ''}
            </div>
            <div>
              <span className="font-medium">
                <FormattedMessage id={translations.components.orderDetail.cancellation.message} />
              </span>{' '}
              {cancellationReason.errorDetail?.message ?? '-'}
            </div>
          </div>

          {!!cancellationReason.lineErrors?.length && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[28rem] text-left text-sm">
                <thead>
                  <tr className="border-b border-error/20">
                    <th className="py-2 pr-3 font-semibold">
                      <FormattedMessage id={translations.components.orderDetail.cancellation.productCode} />
                    </th>
                    <th className="py-2 pr-3 font-semibold">
                      <FormattedMessage id={translations.components.orderDetail.cancellation.errorCode} />
                    </th>
                    <th className="py-2 font-semibold">
                      <FormattedMessage id={translations.components.orderDetail.cancellation.errorMessage} />
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {cancellationReason.lineErrors.map((lineError, index) => (
                    <tr className="border-b border-error/10 last:border-b-0" key={`${lineError.productCode ?? 'line-error'}-${index}`}>
                      <td className="py-2 pr-3">{lineError.productCode ?? '-'}</td>
                      <td className="py-2 pr-3">
                        {lineError.errorCode ?? '-'}
                        {lineError.errorCode ? ` (${getTranslatedErrorCode(lineError.errorCode)})` : ''}
                      </td>
                      <td className="py-2">{lineError.errorMessage || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </main>
  );
};
